import * as React from 'react';
import { useState, useCallback } from 'react';
import Menu, { type MenuProps } from '@mui/material/Menu';
import MuiIconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';

export interface IconMenuProps extends Omit<MenuProps, 'open' | 'anchorEl' | 'onClose'> {
  /** react-toolbox: icon to display (material icon name) */
  icon?: React.ReactNode;
  /** react-toolbox: icon font class */
  iconRipple?: boolean;
  /** react-toolbox: menu position */
  position?: 'auto' | 'static' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  /** react-toolbox: callback when menu is shown */
  onShow?: () => void;
  /** react-toolbox: callback when menu is hidden */
  onHide?: () => void;
  /** Click handler for the icon button */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Additional class name */
  className?: string;
}

/**
 * IconMenu wrapper - maps react-toolbox IconMenu to MUI IconButton + Menu
 * - Manages anchor state internally
 * - `icon` renders the icon button trigger
 * - `position` is mapped to MUI anchor origin
 * - Children are rendered in the Menu
 */
const IconMenu = React.forwardRef<HTMLButtonElement, IconMenuProps>(
  (
    { icon = 'more_vert', iconRipple = true, position = 'auto', onShow, onHide, onClick, className, children, ...rest },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        if (onClick) {
          onClick(event);
        }
        if (onShow) {
          onShow();
        }
      },
      [onClick, onShow],
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
      if (onHide) {
        onHide();
      }
    }, [onHide]);

    // Map position to MUI anchor origin
    const getAnchorOrigin = () => {
      switch (position) {
        case 'topLeft':
          return { vertical: 'top' as const, horizontal: 'left' as const };
        case 'topRight':
          return { vertical: 'top' as const, horizontal: 'right' as const };
        case 'bottomLeft':
          return { vertical: 'bottom' as const, horizontal: 'left' as const };
        case 'bottomRight':
          return { vertical: 'bottom' as const, horizontal: 'right' as const };
        default:
          return { vertical: 'bottom' as const, horizontal: 'left' as const };
      }
    };

    // Render icon
    const iconElement = typeof icon === 'string' ? <Icon>{icon}</Icon> : icon;

    return (
      <>
        <MuiIconButton
          ref={ref}
          className={className}
          onClick={handleClick}
          disableRipple={!iconRipple}
          aria-controls={open ? 'icon-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          {iconElement}
        </MuiIconButton>
        <Menu
          id="icon-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={getAnchorOrigin()}
          onClick={handleClose}
          {...rest}
        >
          {children}
        </Menu>
      </>
    );
  },
);

IconMenu.displayName = 'IconMenu';

export default IconMenu;
